<template>
  <div class="step-pane fade-in">
    <uploader-card
      v-if="uploadConfig"
      ref="videoUploader"
      :upload-config="uploadConfig"
      @before-upload="handleVideoAdded"
      @success="handleVideoSuccess"
    />
    <div v-if="!isSingle" class="step-actions center">
      <el-button
        type="primary"
        size="medium"
        :disabled="!value.videoId"
        suffix-icon="el-icon-arrow-right"
        @click="$emit('next')"
      >
        下一步：设置封面 {{ !value.videoId ? '(请先上传视频)' : '' }}
      </el-button>
    </div>
  </div>
</template>

<script>
import UploaderCard from 'components/card/UploaderCard.vue'
import { addVideoFile, updateVideoFile } from '@/api/vod'
import { getVideoChannelInfo } from '@/api/file'

export default {
  name: 'StepUploadVideo',
  components: { UploaderCard },
  props: {
    value: { type: Object, required: true },
    isEdit: { type: Boolean, default: false },
    // 🌟 新增：是否作为独立组件单页使用
    isSingle: { type: Boolean, default: false }
  },
  data() {
    return {
      uploadConfig: null
    }
  },
  created() {
    this.initUploadConfig()
  },
  methods: {
    initUploadConfig() {
      getVideoChannelInfo().then(resp => {
        if (resp.code === 0) {
          this.uploadConfig = resp.data
          this.uploadConfig.title = this.isEdit ? '重新上传视频文件' : '上传视频文件'
          this.uploadConfig.fileAttrs = { accept: 'video/*' }
        } else {
          this.$message.warning(resp.msg)
        }
      })
    },
    handleVideoAdded(filename) {
      if (!this.isEdit && !this.value.title) {
        // 直接修改父组件传进来的引用对象属性
        this.value.title = filename.replace(/\.[^/.]+$/, '').substring(0, 50)
      }
    },
    handleVideoSuccess(uploadResult) {
      const { uploadId, file } = uploadResult
      if (this.isEdit) {
        updateVideoFile({
          videoId: this.value.videoId,
          videoFileId: uploadId,
          filename: file.name
        }).then(res => {
          this.$message.success('视频资产更新成功')
          // 🌟 修改：只有在非独立单页时，才触发 next 自动流转步骤
          if (!this.isSingle) {
            this.$emit('next')
          } else {
            // 独立使用时，抛出通知让列表页感知刷新
            this.$emit('success')
          }
        })
      } else {
        addVideoFile({
          videoFileId: uploadId,
          filename: file.name
        }).then(resp => {
          if (resp.code === 0) {
            this.value.videoId = resp.data
            this.$message.success('视频文件上传成功！')

            // 核心改动：把原生的本地 File 对象抛给父组件
            this.$emit('video-success', file.file)
            this.$emit('next')
          } else {
            this.$message.warning(resp.msg)
          }
        })
      }
    }
  }
}
</script>

<style scoped lang="scss">
.video-post-container {
  padding: 32px 20px;
  background-color: #f6f8fa;
  min-height: 100vh;
  box-sizing: border-box;
  display: flex;
  justify-content: center;

  .steps-wrapper {
    width: 100%;
    max-width: 880px; // 限制表单总宽度，防止宽屏下横向拉伸过长导致视觉松散
    border: 1px solid #eaeefb;
    border-radius: 12px;
    box-shadow: 0 6px 16px -4px rgba(0, 0, 0, 0.04);
    padding: 12px;
  }

  .custom-steps {
    margin-bottom: 40px;
    ::v-deep .el-step__title {
      font-size: 14px;
      font-weight: 500;
    }
  }

  // 步骤内容切换区域
  .step-content-router {
    min-height: 320px;
  }

  // 淡入动效
  .fade-in {
    animation: fadeIn 0.35s ease-in-out;
  }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: translateY(0); }
  }

  // 统一的下方操作栏
  .step-actions {
    margin-top: 36px;
    padding-top: 20px;
    border-top: 1px solid #f0f2f5;
    display: flex;
    justify-content: space-between;
    align-items: center;

    &.center {
      justify-content: center;
    }
  }
}

/* 封面上传样式优化 */
.cover-upload-wrapper {
  display: flex;
  gap: 24px;
  align-items: flex-start;
  padding: 10px 0;

  .avatar-uploader {
    border: 2px dashed #e0e0e0;
    border-radius: 10px;
    cursor: pointer;
    overflow: hidden;
    width: 260px;
    height: 155px;
    background: #fafafa;
    transition: all 0.25s ease-in-out;
    flex-shrink: 0;

    &:hover {
      border-color: #409EFF;
      background: #f5f9ff;
    }
  }

  .uploader-placeholder {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100%;
    color: #909399;

    .placeholder-icon { font-size: 26px; margin-bottom: 8px; }
    .placeholder-text { font-size: 13px; }
  }

  .cover-preview {
    position: relative;
    width: 100%;
    height: 100%;
    .avatar { width: 100%; height: 100%; object-fit: cover; }
    .cover-mask {
      position: absolute; top: 0; left: 0; width: 100%; height: 100%;
      background: rgba(29, 33, 41, 0.6); color: #fff;
      display: flex; flex-direction: column; justify-content: center; align-items: center;
      gap: 6px; font-size: 13px; opacity: 0; transition: opacity 0.2s ease;
    }
    &:hover .cover-mask { opacity: 1; }
  }

  .cover-info-text {
    flex: 1;

    .tip-title {
      font-size: 14px;
      font-weight: 500;
      color: #606266;
      margin-bottom: 10px;
      display: flex;
      align-items: center;
      gap: 4px;
      i { color: #409EFF; }
    }
    .tip-item { font-size: 13px; color: #8c929e; margin: 6px 0; line-height: 1.6; }
  }
}

/* 表单精细化调整 */
.custom-form {
  ::v-deep .el-form-item__label {
    padding-bottom: 6px;
    font-weight: 500;
    color: #303133;
  }

  .custom-radio-group {
    width: 100%;
    display: flex;
    ::v-deep .el-radio-button {
      flex: 1;
      .el-radio-button__inner { width: 100%; text-align: center; }
    }
  }

  .tags-select {
    ::v-deep .el-select__tags { max-height: 80px; overflow-y: auto; }
  }
}
</style>
